import { ApiError, notifyUnauthorized } from './client';
import { csrfHeadersForRequest } from './csrf';
import { i18next } from '../i18n';
import { notifySedrDeletionPendingChanged } from '../pages/sedr/sedrDeletionPending';

export interface SedrDashboardMetrics {
  totalRequirements: number;
  uploadedEvidence: number;
  pendingApproval: number;
  approved: number;
}

export interface SedrRoomSummary {
  id: string;
  serverToken: string;
  title: string;
  clientOrganization: string;
  lifecycleState: string;
  permission: string;
  createdAt: string;
  deletionScheduledAt: string | null;
  purgeAt: string | null;
  adminCount: number;
  fileCount: number;
  requirementCount: number;
  memberCount: number;
  guestInviteCount: number;
  deletionRequestedBy: string | null;
  deletionRequestedByName: string | null;
  deletionRequestedAt: string | null;
  archivedAt: string | null;
  lastModifiedAt: string;
  lastViewedAt: string | null;
  requireGuestTotp?: boolean;
  roomType?: string;
  allowGuestLinks?: boolean;
  canApprove?: boolean;
  deletionReason?: string | null;
}

export interface SedrRoomDetail extends SedrRoomSummary {
  canManageMembers: boolean;
  canScheduleDeletion: boolean;
  wrappedRoomKey: string;
  metrics: SedrDashboardMetrics;
}

export interface SedrMember {
  userId: string;
  displayName: string;
  permission: string;
  createdAt: string;
  email?: string | null;
  canApprove?: boolean;
}

export interface SedrGuestInvite {
  id: string;
  displayName: string;
  organization: string;
  email: string | null;
  revokedAt: string | null;
  createdAt: string;
  expiresAt?: string | null;
  totpEnrolled?: boolean;
  guestRole?: string;
  allowCommenting?: boolean;
  accessMode?: string | null;
  claimedAt?: string | null;
}

export interface SedrReportSchedule {
  enabled: boolean;
  cadence: 'Weekly' | 'Monthly' | 'Yearly' | string;
  weekday: number | null;
  dayOfMonth: number | null;
  month: number | null;
  sendTimeUtc: string;
  locale: string;
  pdfFormat: 'Summary' | 'Detailed' | string;
  includeObsolete: boolean;
  lastSentAt: string | null;
  nextSendAt: string | null;
}

export interface PutSedrReportSchedulePayload {
  enabled: boolean;
  cadence: string;
  weekday?: number | null;
  dayOfMonth?: number | null;
  month?: number | null;
  sendTimeUtc: string;
  locale?: string | null;
  pdfFormat?: 'Summary' | 'Detailed' | string;
  includeObsolete?: boolean;
}

export interface SedrReportRecipient {
  id: string;
  email: string;
  displayName: string;
  userId: string | null;
  guestInviteId: string | null;
  optedOut: boolean;
  optedOutAt: string | null;
  createdAt: string;
}

export interface SedrReportSendResult {
  recipientCount: number;
  sentCount: number;
  manual: boolean;
}

export interface SedrFile {
  id: string;
  name: string;
  /** Omitted by API when null (WhenWritingNull); treat missing as root. */
  parentId?: string | null;
  isDirectory: boolean;
  blobId: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  createdAt: string;
  wrappedContentKey?: string | null;
  encryptionFileId?: string | null;
  contentHash?: string | null;
  chunkPlaintextBytes?: number | null;
  chunkCount?: number | null;
  chunkCiphertextSizes?: string | null;
  versionOfFileId?: string | null;
  versionNumber?: number;
}

export interface SedrArtifactBadge {
  id: string;
  fileId: string;
  name: string;
  contentType: string | null;
  archivedAt: string | null;
}

export interface SedrRequirement {
  id: string;
  requirementCode: string;
  title: string;
  description: string | null;
  category: string;
  categoryName?: string | null;
  status: string;
  ownerName: string | null;
  testMethodFrequency: string | null;
  lastTestDate: string | null;
  linkedFileId: string | null;
  linkedFileName: string | null;
  artifacts: SedrArtifactBadge[];
  approvedAt: string | null;
  approvedByDisplay: string | null;
  canClientApprove: boolean;
  phaseId?: string | null;
  phaseName?: string | null;
  approvalValidityAmount?: number | null;
  approvalValidityUnit?: string | null;
  approvalValidityDays?: number | null;
  approvalExpiresAt?: string | null;
  approvalGraceDays?: number | null;
  renewalStatus?: string | null;
  inApprovalGrace?: boolean;
  canApprove?: boolean;
  requestedLifecycleStatus?: string | null;
  supersededByRequirementId?: string | null;
  supersededByRequirementCode?: string | null;
  links?: SedrRequirementLink[];
}

export interface SedrRequirementLink {
  id: string;
  direction: 'outgoing' | 'incoming';
  linkType: string;
  linkTypeName: string;
  otherRequirementId: string;
  otherRequirementCode: string;
  otherTitle: string;
  otherStatus: string;
}

export interface SedrRequirementLinkType {
  id: string;
  name: string;
  inverseName: string | null;
  sortOrder: number;
  isBuiltIn?: boolean;
}

export interface CreateSedrRequirementLinkPayload {
  toRequirementId: string;
  linkType: string;
}

export interface CreateSedrRequirementLinkTypePayload {
  name: string;
  inverseName?: string | null;
  sortOrder?: number | null;
}

export interface PatchSedrRequirementLinkTypePayload {
  name?: string | null;
  inverseName?: string | null;
  sortOrder?: number | null;
  clearInverseName?: boolean;
}

export interface SedrPhaseProgress {
  totalAssigned: number;
  pendingUpload: number;
  underReview: number;
  approved: number;
  completionPercent: number;
}

export interface SedrPhase {
  id: string;
  name: string;
  startAt: string | null;
  endAt: string | null;
  sortOrder: number;
  progress: SedrPhaseProgress;
  archivedAt?: string | null;
}

export interface SedrPhasesList {
  phases: SedrPhase[];
  unassignedRequirementCount: number;
}

export interface CreateSedrPhasePayload {
  name: string;
  startAt?: string | null;
  endAt?: string | null;
  sortOrder?: number | null;
}

export interface PatchSedrPhasePayload {
  name?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  sortOrder?: number | null;
  clearStartAt?: boolean;
  clearEndAt?: boolean;
}

export interface SedrComment {
  id: string;
  requirementId: string;
  body: string;
  authorUserId: string | null;
  authorGuestInviteId: string | null;
  authorDisplayName: string | null;
  createdAt: string;
  causesStatusChange?: boolean;
  resultingStatus?: string | null;
  declinesLifecycleChange?: boolean;
  relatedRequirementCode?: string | null;
}

export interface SedrAuditEvent {
  id: string;
  action: string;
  actorDisplay: string | null;
  documentId: string | null;
  detailsJson: string | null;
  createdAt: string;
}

export interface SedrGateResponse {
  nonce: string;
  passwordRequired: boolean;
  passwordMode: string;
  expired: boolean;
  revoked: boolean;
  roomId: string;
  title: string;
  clientOrganization: string;
  guestDisplayName: string;
  guestOrganization: string;
  deletionScheduledAt: string | null;
  purgeAt: string | null;
  requireGuestTotp?: boolean;
  totpEnrolled?: boolean;
  guestRole?: string;
  allowCommenting?: boolean;
  roomType?: string;
  /** Invite access expiry; null/undefined means no expiry. */
  expiresAt?: string | null;
  guestEmail?: string | null;
}

export interface SedrVerifyResponse {
  ok: boolean;
  nextStep?: string | null;
  pendingToken?: string | null;
  totpSecret?: string | null;
  otpauthUri?: string | null;
}

export interface SedrGuestRoomContext {
  roomId: string;
  title: string;
  clientOrganization: string;
  lifecycleState: string;
  deletionScheduledAt: string | null;
  purgeAt: string | null;
  guestDisplayName: string;
  guestOrganization: string;
  guestEmail: string | null;
  deletionRequestedByName: string | null;
  metrics: SedrDashboardMetrics;
  guestRole?: string;
  allowCommenting?: boolean;
  requireGuestTotp?: boolean;
  roomType?: string;
  canApprove?: boolean;
  deletionReason?: string | null;
}

export interface CreateSedrRequirementPayload {
  requirementCode: string;
  title: string;
  description?: string | null;
  category: string;
  ownerName?: string | null;
  testMethodFrequency?: string | null;
  lastTestDate?: string | null;
  status?: string | null;
  linkedFileId?: string | null;
  phaseId?: string | null;
  approvalValidityAmount?: number | null;
  approvalValidityUnit?: string | null;
  approvalValidityDays?: number | null;
  approvalGraceDays?: number | null;
}

export interface PatchSedrRequirementPayload {
  title?: string | null;
  /** Empty string clears; omit to leave unchanged. */
  description?: string | null;
  status?: string | null;
  /** Empty string clears; omit to leave unchanged. */
  ownerName?: string | null;
  testMethodFrequency?: string | null;
  lastTestDate?: string | null;
  linkedFileId?: string | null;
  /** Empty string clears assignment; omit to leave unchanged. */
  phaseId?: string | null;
  category?: string | null;
  approvalValidityAmount?: number | null;
  approvalValidityUnit?: string | null;
  clearApprovalValidity?: boolean;
  statusChangeReason?: string | null;
  approvalValidityDays?: number | null;
  clearApprovalValidityDays?: boolean;
  requestedLifecycleStatus?: string | null;
  clearRequestedLifecycleStatus?: boolean;
  supersededByRequirementId?: string | null;
  approvalGraceDays?: number | null;
  clearApprovalGraceDays?: boolean;
}

export interface BulkPatchSedrRequirementsPayload {
  requirementIds: string[];
  category?: string | null;
  /** Present to set/clear; omit to leave unchanged. Empty string clears. */
  phaseId?: string | null;
  status?: string | null;
  statusChangeReason?: string | null;
}

export interface SedrCategory {
  id: string;
  name: string;
  sortOrder: number;
}

export type SedrStarterSetType = 'Requirements' | 'Folders';

export interface SedrStarterSetSummary {
  id: string;
  name: string;
  description: string | null;
  setType: SedrStarterSetType;
  itemCount: number;
  updatedAt: string;
}

export interface SedrStarterSetItem {
  id: string;
  requirementCode: string;
  title: string;
  description: string | null;
  category: string;
  ownerName: string | null;
  testMethodFrequency: string | null;
  sortOrder: number;
}

export interface SedrStarterSetFolder {
  id: string;
  parentId: string | null;
  name: string;
  sortOrder: number;
}

export interface SedrStarterSetDetail {
  id: string;
  name: string;
  description: string | null;
  setType: SedrStarterSetType;
  items: SedrStarterSetItem[];
  folders: SedrStarterSetFolder[];
  updatedAt: string;
}

export interface CreateSedrFilePayload {
  name: string;
  parentId?: string | null;
  isDirectory: boolean;
  blobId?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  wrappedContentKey?: string | null;
  encryptionFileId?: string | null;
  contentHash?: string | null;
  chunkPlaintextBytes?: number | null;
  chunkCount?: number | null;
  chunkCiphertextSizes?: string | null;
  versionOfFileId?: string | null;
  versionNumber?: number | null;
}

async function sedrFetch(path: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
      ...csrfHeadersForRequest(path, init?.method),
    },
    credentials: 'include',
  });
  if (!response.ok) {
    notifyUnauthorized(response.status);
    const body = await response.json().catch(() => ({ code: 'request_failed' }));
    throw new ApiError(body.code ?? 'request_failed', undefined, response.status);
  }
  return response;
}

async function sedrRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await sedrFetch(path, init);
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

async function sedrRequestText(path: string, init?: RequestInit): Promise<string> {
  const response = await sedrFetch(path, init);
  return response.text();
}

export const sedrApi = {
  listRooms: (archived = false) =>
    sedrRequest<SedrRoomSummary[]>(`/api/sedr/rooms${archived ? '?archived=true' : ''}`),
  createRoom: (payload: {
    title: string;
    clientOrganization: string;
    wrappedRoomKey: string;
    secondAdminUserId?: string;
    secondAdminWrappedRoomKey?: string;
    roomType: string;
    requireGuestTotp?: boolean;
    allowGuestLinks?: boolean;
    requirementsStarterSetId?: string | null;
    folderStarterSetId?: string | null;
  }) =>
    sedrRequest<{ id: string; serverToken: string; guestPathPrefix: string; lifecycleState: string }>(
      '/api/sedr/rooms',
      { method: 'POST', body: JSON.stringify(payload) },
    ),
  getRoom: (id: string) => sedrRequest<SedrRoomDetail>(`/api/sedr/rooms/${id}`),
  patchRoom: (id: string, payload: {
    title?: string;
    clientOrganization?: string;
    requireGuestTotp?: boolean;
    allowGuestLinks?: boolean;
  }) =>
    sedrRequest<SedrRoomDetail>(`/api/sedr/rooms/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteRoom: (id: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}`, { method: 'DELETE' }),
  listMembers: (id: string) => sedrRequest<SedrMember[]>(`/api/sedr/rooms/${id}/members`),
  inviteMember: (id: string, payload: {
    userId: string;
    permission: string;
    wrappedRoomKey: string;
    canApprove?: boolean;
  }) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/members`, { method: 'POST', body: JSON.stringify(payload) }),
  patchMember: (id: string, userId: string, payload: { permission?: string; canApprove?: boolean }) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/members/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  removeMember: (id: string, userId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/members/${encodeURIComponent(userId)}`, { method: 'DELETE' }),
  listGuestInvites: (id: string) => sedrRequest<SedrGuestInvite[]>(`/api/sedr/rooms/${id}/guest-invites`),
  lookupGuestEmail: (id: string, email: string) =>
    sedrRequest<{
      outcome: string;
      userId?: string;
      userPublicKey?: string;
      displayName?: string;
      organization?: string;
    }>(`/api/sedr/rooms/${id}/guest-email-lookup?email=${encodeURIComponent(email)}`),
  createGuestInvite: (id: string, payload: Record<string, unknown>) =>
    sedrRequest<{
      outcome: string;
      guestInviteId?: string;
      guestPath?: string;
      userId?: string;
      displayName?: string;
    }>(`/api/sedr/rooms/${id}/guest-invites`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchGuestInvite: (
    id: string,
    inviteId: string,
    payload: {
      expiresAt?: string | null;
      clearExpiresAt?: boolean;
      guestRole?: string;
      allowCommenting?: boolean;
      email?: string;
    },
  ) =>
    sedrRequest<SedrGuestInvite>(`/api/sedr/rooms/${id}/guest-invites/${encodeURIComponent(inviteId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  revokeGuestInvite: (id: string, inviteId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/guest-invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE' }),
  resetGuestTotp: (id: string, inviteId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/guest-invites/${encodeURIComponent(inviteId)}/reset-totp`, {
      method: 'POST',
    }),
  guestUpgradeStart: (roomToken: string, guestInviteId: string) =>
    sedrRequest<{
      inviteId: string;
      roomId: string;
      roomServerToken: string;
      envelopeBlobId: string;
      displayName: string;
      organization: string;
      email: string;
      guestRole: string;
      allowCommenting: boolean;
      totpEnrolled: boolean;
      canMigrateTotp: boolean;
    }>(`/api/sedr/${roomToken}/g/${guestInviteId}/upgrade/start`, { method: 'POST' }),
  scheduleDeletion: async (id: string, reason: string) => {
    await sedrRequest<void>(`/api/sedr/rooms/${id}/deletion`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    notifySedrDeletionPendingChanged();
  },
  decideDeletion: async (id: string, approve: boolean) => {
    await sedrRequest<void>(`/api/sedr/rooms/${id}/deletion/decision`, {
      method: 'POST',
      body: JSON.stringify({ approve }),
    });
    notifySedrDeletionPendingChanged();
  },
  cancelDeletion: async (id: string) => {
    await sedrRequest<void>(`/api/sedr/rooms/${id}/deletion`, { method: 'DELETE' });
    notifySedrDeletionPendingChanged();
  },
  archiveRoom: (id: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/archive`, { method: 'POST' }),
  unarchiveRoom: (id: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/unarchive`, { method: 'POST' }),
  listFiles: (id: string) => sedrRequest<SedrFile[]>(`/api/sedr/rooms/${id}/files`),
  addFile: (id: string, payload: CreateSedrFilePayload) =>
    sedrRequest<SedrFile>(`/api/sedr/rooms/${id}/files`, { method: 'POST', body: JSON.stringify(payload) }),
  patchFile: (id: string, fileId: string, payload: { name: string; parentId: string | null }) =>
    sedrRequest<SedrFile>(`/api/sedr/rooms/${id}/files/${encodeURIComponent(fileId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  recordFileOpened: (id: string, fileId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/files/${fileId}/opened`, { method: 'POST' }),
  listRequirements: (id: string, params?: { status?: string; category?: string }) => {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.category) query.set('category', params.category);
    const qs = query.toString();
    return sedrRequest<SedrRequirement[]>(`/api/sedr/rooms/${id}/requirements${qs ? `?${qs}` : ''}`);
  },
  createRequirement: (id: string, payload: CreateSedrRequirementPayload) =>
    sedrRequest<SedrRequirement>(`/api/sedr/rooms/${id}/requirements`, { method: 'POST', body: JSON.stringify(payload) }),
  importRequirements: (id: string, rows: Record<string, unknown>[]) =>
    sedrRequest<{ created: number; skipped: number; errors: string[] }>(`/api/sedr/rooms/${id}/requirements/import`, {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
  patchRequirement: (id: string, requirementId: string, payload: PatchSedrRequirementPayload) =>
    sedrRequest<SedrRequirement>(`/api/sedr/rooms/${id}/requirements/${requirementId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  bulkPatchRequirements: (id: string, payload: BulkPatchSedrRequirementsPayload) =>
    sedrRequest<SedrRequirement[]>(`/api/sedr/rooms/${id}/requirements/bulk`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  listPhases: (id: string, options?: { archived?: boolean }) =>
    sedrRequest<SedrPhasesList>(
      `/api/sedr/rooms/${id}/phases${options?.archived ? '?archived=true' : ''}`,
    ),
  createPhase: (id: string, payload: CreateSedrPhasePayload) =>
    sedrRequest<SedrPhase>(`/api/sedr/rooms/${id}/phases`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchPhase: (id: string, phaseId: string, payload: PatchSedrPhasePayload) =>
    sedrRequest<SedrPhase>(`/api/sedr/rooms/${id}/phases/${encodeURIComponent(phaseId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  archivePhase: (id: string, phaseId: string) =>
    sedrRequest<SedrPhase>(
      `/api/sedr/rooms/${id}/phases/${encodeURIComponent(phaseId)}/archive`,
      { method: 'POST' },
    ),
  unarchivePhase: (id: string, phaseId: string) =>
    sedrRequest<SedrPhase>(
      `/api/sedr/rooms/${id}/phases/${encodeURIComponent(phaseId)}/unarchive`,
      { method: 'POST' },
    ),
  listRequirementLinkTypes: (id: string) =>
    sedrRequest<SedrRequirementLinkType[]>(`/api/sedr/rooms/${id}/link-types`),
  createRequirementLinkType: (id: string, payload: CreateSedrRequirementLinkTypePayload) =>
    sedrRequest<SedrRequirementLinkType>(`/api/sedr/rooms/${id}/link-types`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchRequirementLinkType: (id: string, typeId: string, payload: PatchSedrRequirementLinkTypePayload) =>
    sedrRequest<SedrRequirementLinkType>(`/api/sedr/rooms/${id}/link-types/${encodeURIComponent(typeId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  createRequirementLink: (id: string, requirementId: string, payload: CreateSedrRequirementLinkPayload) =>
    sedrRequest<SedrRequirementLink>(`/api/sedr/rooms/${id}/requirements/${requirementId}/links`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  deleteRequirementLink: (id: string, requirementId: string, linkId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/requirements/${requirementId}/links/${linkId}`, {
      method: 'DELETE',
    }),
  linkArtifact: (id: string, requirementId: string, fileId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/requirements/${requirementId}/artifacts`, {
      method: 'POST',
      body: JSON.stringify({ fileId }),
    }),
  archiveArtifact: (id: string, requirementId: string, artifactId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/requirements/${requirementId}/artifacts/${artifactId}/archive`, {
      method: 'POST',
    }),
  unarchiveArtifact: (id: string, requirementId: string, artifactId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/requirements/${requirementId}/artifacts/${artifactId}/unarchive`, {
      method: 'POST',
    }),
  listComments: (id: string, requirementId: string) =>
    sedrRequest<SedrComment[]>(`/api/sedr/rooms/${id}/requirements/${requirementId}/comments`),
  searchRequirementsByComment: (id: string, q: string) =>
    sedrRequest<{ requirementIds: string[] }>(`/api/sedr/rooms/${id}/requirements/comment-search`, {
      method: 'POST',
      body: JSON.stringify({ query: q }),
    }),
  addComment: (id: string, requirementId: string, body: string) =>
    sedrRequest<SedrComment>(`/api/sedr/rooms/${id}/requirements/${requirementId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  listAuditEvents: (id: string) => sedrRequest<SedrAuditEvent[]>(`/api/sedr/rooms/${id}/audit-events`),

  getReportSchedule: (id: string) =>
    sedrRequest<SedrReportSchedule>(`/api/sedr/rooms/${id}/report-schedule`),
  putReportSchedule: (id: string, payload: PutSedrReportSchedulePayload) =>
    sedrRequest<SedrReportSchedule>(`/api/sedr/rooms/${id}/report-schedule`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  sendReportNow: (id: string) =>
    sedrRequest<SedrReportSendResult>(`/api/sedr/rooms/${id}/report-schedule/send-now`, {
      method: 'POST',
    }),
  listReportRecipients: (id: string) =>
    sedrRequest<SedrReportRecipient[]>(`/api/sedr/rooms/${id}/report-recipients`),
  addReportRecipient: (id: string, payload: {
    email: string;
    displayName: string;
    userId?: string | null;
    guestInviteId?: string | null;
  }) =>
    sedrRequest<SedrReportRecipient>(`/api/sedr/rooms/${id}/report-recipients`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  deleteReportRecipient: (id: string, recipientId: string) =>
    sedrRequest<void>(`/api/sedr/rooms/${id}/report-recipients/${encodeURIComponent(recipientId)}`, {
      method: 'DELETE',
    }),
  downloadComplianceReportPdf: async (id: string, includeObsolete = false): Promise<void> => {
    const params = new URLSearchParams();
    if (includeObsolete) params.set('includeObsolete', 'true');
    const locale = i18next.language?.slice(0, 2);
    if (locale) params.set('locale', locale);
    const query = params.toString() ? `?${params}` : '';
    const response = await sedrFetch(`/api/sedr/rooms/${id}/compliance-report.pdf${query}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'sedr-compliance-report.pdf';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
  downloadComplianceReportDetailedPdf: async (id: string, includeObsolete = false): Promise<void> => {
    const params = new URLSearchParams();
    if (includeObsolete) params.set('includeObsolete', 'true');
    const locale = i18next.language?.slice(0, 2);
    if (locale) params.set('locale', locale);
    const query = params.toString() ? `?${params}` : '';
    const response = await sedrFetch(`/api/sedr/rooms/${id}/compliance-report-detailed.pdf${query}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'sedr-compliance-report-detailed.pdf';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },

  listCategories: (roomId: string) =>
    sedrRequest<SedrCategory[]>(`/api/sedr/rooms/${encodeURIComponent(roomId)}/categories`),
  createCategory: (roomId: string, payload: { name: string; sortOrder?: number }) =>
    sedrRequest<SedrCategory>(`/api/sedr/rooms/${encodeURIComponent(roomId)}/categories`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchCategory: (roomId: string, id: string, payload: { name?: string; sortOrder?: number }) =>
    sedrRequest<SedrCategory>(
      `/api/sedr/rooms/${encodeURIComponent(roomId)}/categories/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    ),
  deleteCategory: (roomId: string, id: string) =>
    sedrRequest<void>(
      `/api/sedr/rooms/${encodeURIComponent(roomId)}/categories/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  listStarterSets: (setType: SedrStarterSetType = 'Requirements') =>
    sedrRequest<SedrStarterSetSummary[]>(
      `/api/sedr/starter-sets?type=${encodeURIComponent(setType)}`,
    ),
  getStarterSet: (id: string) => sedrRequest<SedrStarterSetDetail>(`/api/sedr/starter-sets/${encodeURIComponent(id)}`),
  createStarterSet: (payload: {
    name: string;
    description?: string | null;
    setType?: SedrStarterSetType;
  }) =>
    sedrRequest<SedrStarterSetDetail>('/api/sedr/starter-sets', { method: 'POST', body: JSON.stringify(payload) }),
  patchStarterSet: (id: string, payload: { name?: string; description?: string | null }) =>
    sedrRequest<SedrStarterSetDetail>(`/api/sedr/starter-sets/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteStarterSet: (id: string) =>
    sedrRequest<void>(`/api/sedr/starter-sets/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  createStarterSetItem: (setId: string, payload: {
    requirementCode: string;
    title: string;
    description?: string | null;
    category: string;
    ownerName?: string | null;
    testMethodFrequency?: string | null;
    sortOrder?: number;
  }) =>
    sedrRequest<SedrStarterSetDetail>(`/api/sedr/starter-sets/${encodeURIComponent(setId)}/items`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchStarterSetItem: (setId: string, itemId: string, payload: Record<string, unknown>) =>
    sedrRequest<SedrStarterSetDetail>(
      `/api/sedr/starter-sets/${encodeURIComponent(setId)}/items/${encodeURIComponent(itemId)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    ),
  deleteStarterSetItem: (setId: string, itemId: string) =>
    sedrRequest<void>(
      `/api/sedr/starter-sets/${encodeURIComponent(setId)}/items/${encodeURIComponent(itemId)}`,
      { method: 'DELETE' },
    ),
  importStarterSetItems: (setId: string, rows: Record<string, unknown>[]) =>
    sedrRequest<{ created: number; skipped: number; errors: string[] }>(
      `/api/sedr/starter-sets/${encodeURIComponent(setId)}/items/import`,
      {
        method: 'POST',
        body: JSON.stringify({ rows }),
      },
    ),
  createStarterSetFolder: (setId: string, payload: {
    name: string;
    parentId?: string | null;
    sortOrder?: number;
  }) =>
    sedrRequest<SedrStarterSetDetail>(`/api/sedr/starter-sets/${encodeURIComponent(setId)}/folders`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchStarterSetFolder: (setId: string, folderId: string, payload: {
    name?: string;
    parentId?: string | null;
    sortOrder?: number;
  }) =>
    sedrRequest<SedrStarterSetDetail>(
      `/api/sedr/starter-sets/${encodeURIComponent(setId)}/folders/${encodeURIComponent(folderId)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    ),
  deleteStarterSetFolder: (setId: string, folderId: string) =>
    sedrRequest<void>(
      `/api/sedr/starter-sets/${encodeURIComponent(setId)}/folders/${encodeURIComponent(folderId)}`,
      { method: 'DELETE' },
    ),
  applyFolderStarterSet: (roomId: string, starterSetId: string) =>
    sedrRequest<{ created: number; reused: number }>(
      `/api/sedr/rooms/${encodeURIComponent(roomId)}/folders/from-starter-set`,
      { method: 'POST', body: JSON.stringify({ starterSetId }) },
    ),

  guestGate: (roomToken: string, guestInviteId: string) =>
    sedrRequestText(`/api/sedr/${roomToken}/g/${guestInviteId}/gate`),
  guestVerify: (roomToken: string, guestInviteId: string, payload: { signature: string; password?: string }) =>
    sedrRequest<SedrVerifyResponse>(`/api/sedr/${roomToken}/g/${guestInviteId}/verify`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  guestTotpConfirm: (
    roomToken: string,
    guestInviteId: string,
    payload: { pendingToken: string; code: string },
  ) =>
    sedrRequest<SedrVerifyResponse>(`/api/sedr/${roomToken}/g/${guestInviteId}/totp/confirm`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  guestTotpVerify: (
    roomToken: string,
    guestInviteId: string,
    payload: { pendingToken: string; code: string },
  ) =>
    sedrRequest<SedrVerifyResponse>(`/api/sedr/${roomToken}/g/${guestInviteId}/totp/verify`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  guestKeyEnvelope: (roomToken: string, guestInviteId: string) =>
    sedrRequest<{ envelopeBlobId: string }>(`/api/sedr/${roomToken}/g/${guestInviteId}/key-envelope`),
  guestRoomContext: (roomToken: string, guestInviteId: string) =>
    sedrRequest<SedrGuestRoomContext>(`/api/sedr/${roomToken}/g/${guestInviteId}/room`),
  guestListRequirements: (roomToken: string, guestInviteId: string) =>
    sedrRequest<SedrRequirement[]>(`/api/sedr/${roomToken}/g/${guestInviteId}/requirements`),
  guestListPhases: (roomToken: string, guestInviteId: string) =>
    sedrRequest<SedrPhasesList>(`/api/sedr/${roomToken}/g/${guestInviteId}/phases`),
  guestListCategories: (roomToken: string, guestInviteId: string) =>
    sedrRequest<SedrCategory[]>(`/api/sedr/${roomToken}/g/${guestInviteId}/categories`),
  guestApproveRequirement: (roomToken: string, guestInviteId: string, requirementId: string) =>
    sedrRequest<void>(`/api/sedr/${roomToken}/g/${guestInviteId}/requirements/${requirementId}/approve`, {
      method: 'POST',
    }),
  guestDenyRequirement: (
    roomToken: string,
    guestInviteId: string,
    requirementId: string,
    reason: string,
  ) =>
    sedrRequest<void>(`/api/sedr/${roomToken}/g/${guestInviteId}/requirements/${requirementId}/deny`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  guestBulkApproveRequirements: (
    roomToken: string,
    guestInviteId: string,
    requirementIds: string[],
  ) =>
    sedrRequest<void>(`/api/sedr/${roomToken}/g/${guestInviteId}/requirements/bulk-approve`, {
      method: 'POST',
      body: JSON.stringify({ requirementIds }),
    }),
  guestBulkDenyRequirements: (
    roomToken: string,
    guestInviteId: string,
    requirementIds: string[],
    reason: string,
  ) =>
    sedrRequest<void>(`/api/sedr/${roomToken}/g/${guestInviteId}/requirements/bulk-deny`, {
      method: 'POST',
      body: JSON.stringify({ requirementIds, reason }),
    }),
  guestListFiles: (roomToken: string, guestInviteId: string) =>
    sedrRequest<SedrFile[]>(`/api/sedr/${roomToken}/g/${guestInviteId}/files`),
  guestAddFile: (roomToken: string, guestInviteId: string, payload: CreateSedrFilePayload) =>
    sedrRequest<SedrFile>(`/api/sedr/${roomToken}/g/${guestInviteId}/files`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  guestPatchFile: (
    roomToken: string,
    guestInviteId: string,
    fileId: string,
    payload: { name: string; parentId: string | null },
  ) =>
    sedrRequest<SedrFile>(
      `/api/sedr/${roomToken}/g/${guestInviteId}/files/${encodeURIComponent(fileId)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    ),
  guestRecordFileOpened: (roomToken: string, guestInviteId: string, fileId: string) =>
    sedrRequest<void>(`/api/sedr/${roomToken}/g/${guestInviteId}/files/${fileId}/opened`, { method: 'POST' }),
  guestListAuditEvents: (roomToken: string, guestInviteId: string) =>
    sedrRequest<SedrAuditEvent[]>(`/api/sedr/${roomToken}/g/${guestInviteId}/audit-events`),
  guestListComments: (roomToken: string, guestInviteId: string, requirementId: string) =>
    sedrRequest<SedrComment[]>(
      `/api/sedr/${roomToken}/g/${guestInviteId}/requirements/${requirementId}/comments`,
    ),
  guestSearchRequirementsByComment: (roomToken: string, guestInviteId: string, q: string) =>
    sedrRequest<{ requirementIds: string[] }>(
      `/api/sedr/${roomToken}/g/${guestInviteId}/requirements/comment-search`,
      { method: 'POST', body: JSON.stringify({ query: q }) },
    ),
  guestAddComment: (roomToken: string, guestInviteId: string, requirementId: string, body: string) =>
    sedrRequest<SedrComment>(
      `/api/sedr/${roomToken}/g/${guestInviteId}/requirements/${requirementId}/comments`,
      { method: 'POST', body: JSON.stringify({ body }) },
    ),
};
